/**
 * Formular-, Auswahl- und Rückmeldebausteine (Kopie aus Axis, gekürzt): `Field` mit `Input`/`Select`/`Textarea`,
 * `RadioGroup`/`Radio`, `Checkbox`, `Switch`, `MessageBar`, `Spinner`. Gestaltet in `components.css`; hier nur die Elemente und die Verdrahtung von Beschriftung und Zustand.
 * Es sind **native Elemente**: Tastatur, Formularversand und Hilfsmittel funktionieren, wie der Browser sie kennt.
 *
 * Die Rückmeldung (`MessageBar`) zeigt die Schwere immer mit Symbol, die Farbe ist Beiwerk (Paket: Semantikfarben
 * kommen immer mit Symbol oder Wort).
 */

import { createContext, useContext, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { CheckCircle, Info, Warning, WarningCircle } from '../icons';
import { classNames } from './classNames';

/* ---------- Feld mit Beschriftung ---------- */

const FieldContext = createContext<string | undefined>(undefined);

export interface FieldProps {
  label: ReactNode;
  /** Kurze Erklärung unter dem Eingabeelement. */
  hint?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Beschriftung über einem Eingabeelement; `Input`, `Select` und `Textarea` darin übernehmen die `id`. */
export function Field({ label, hint, className, children }: FieldProps) {
  const id = useId();
  return (
    <FieldContext.Provider value={id}>
      <div className={classNames('ksb-fieldgroup', className)}>
        <label className="ksb-fieldgroup__label ksb-cap-s" htmlFor={id}>
          {label}
        </label>
        {children}
        {hint !== undefined && <span className="ksb-fieldgroup__hint ksb-body-s">{hint}</span>}
      </div>
    </FieldContext.Provider>
  );
}

export function Input({ className, id, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  const fieldId = useContext(FieldContext);
  return (
    <input
      id={id ?? fieldId}
      className={classNames('ksb-txt', 'ksb-txt--fill', className)}
      {...rest}
    />
  );
}

export function Select({ className, id, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  const fieldId = useContext(FieldContext);
  return <select id={id ?? fieldId} className={classNames('ksb-sel', className)} {...rest} />;
}

export function Textarea({ className, id, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const fieldId = useContext(FieldContext);
  return <textarea id={id ?? fieldId} className={classNames('ksb-area', className)} {...rest} />;
}

/* ---------- Auswahl ---------- */

const RadioContext = createContext<{
  name: string;
  value: string;
  onChange: (value: string) => void;
} | null>(null);

export interface RadioGroupProps {
  value: string;
  onChange: (value: string) => void;
  layout?: 'vertical' | 'horizontal';
  'aria-labelledby'?: string;
  'aria-label'?: string;
  className?: string;
  children: ReactNode;
}

export function RadioGroup({
  value,
  onChange,
  layout = 'vertical',
  className,
  children,
  ...aria
}: RadioGroupProps) {
  const name = useId();
  return (
    <RadioContext.Provider value={{ name, value, onChange }}>
      <div
        role="radiogroup"
        className={classNames(
          'ksb-radiogroup',
          layout === 'horizontal' && 'ksb-radiogroup--row',
          className,
        )}
        {...aria}
      >
        {children}
      </div>
    </RadioContext.Provider>
  );
}

export function Radio({
  value,
  label,
  disabled,
}: {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}) {
  const group = useContext(RadioContext);
  if (group === null) throw new Error('Radio steht außerhalb einer RadioGroup');
  return (
    <label className={classNames('ksb-radio', disabled && 'ksb-radio--aus')}>
      <input
        type="radio"
        name={group.name}
        value={value}
        checked={group.value === value}
        disabled={disabled}
        onChange={() => group.onChange(value)}
      />
      <span>{label}</span>
    </label>
  );
}

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
}

/** Kontrollkästchen mit Beschriftung daneben. */
export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={classNames('ksb-radio', className)}>
      <input type="checkbox" className="ksb-cb" {...rest} />
      <span>{label}</span>
    </label>
  );
}

export interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'role'> {
  /** Sichtbare Beschriftung neben dem Schalter (sonst `aria-label` setzen). */
  label?: ReactNode;
}

/** Ein Schalter: ein Kontrollkästchen mit der Rolle `switch`. */
export function Switch({ label, className, ...rest }: SwitchProps) {
  return (
    <label className={classNames('ksb-switch', className)}>
      <input type="checkbox" role="switch" {...rest} />
      <span className="ksb-switch__track" aria-hidden="true" />
      {label !== undefined && <span>{label}</span>}
    </label>
  );
}

/* ---------- Rückmeldung ---------- */

export type MessageIntent = 'info' | 'success' | 'warning' | 'error';

const INTENT_ICON = {
  info: Info,
  success: CheckCircle,
  warning: Warning,
  error: WarningCircle,
} as const;

export function MessageBar({
  intent = 'info',
  title,
  children,
}: {
  intent?: MessageIntent;
  /** Fett gesetzte erste Zeile. */
  title?: ReactNode;
  children?: ReactNode;
}) {
  const Mark = INTENT_ICON[intent];
  return (
    <div
      className={classNames(
        'ksb-banner',
        intent === 'warning' && 'ksb-banner--warning',
        intent === 'error' && 'ksb-banner--error',
      )}
      role={intent === 'error' ? 'alert' : 'status'}
    >
      {intent === 'info' || intent === 'success' ? (
        <Mark className="ksb-banner__mark" weight="light" size={24} aria-hidden="true" />
      ) : (
        <span className="ksb-banner__flag" aria-hidden="true">
          <Mark weight="light" size={16} />
        </span>
      )}
      <div className="ksb-banner__body">
        {title !== undefined && <strong className="ksb-banner__title ksb-cap-m">{title}</strong>}
        {children}
      </div>
    </div>
  );
}

export function Spinner({ label, size = 'md' }: { label?: string; size?: 'sm' | 'md' }) {
  return (
    <div className="ksb-spinnerwrap" role="status" aria-label={label ?? 'Wird geladen'}>
      <span
        className={classNames('ksb-spinner', size === 'sm' && 'ksb-spinner--sm')}
        aria-hidden="true"
      />
      {label !== undefined && <span className="ksb-body-s">{label}</span>}
    </div>
  );
}
